import type { BikePrefs, PricingPrefs } from "../store/persistence";
import { ItineraryPricingPreferencesDTO } from "../types/dtos/api/pricing/itinerary-pricing-preferences.dto";

/**
 * Maps persisted UI pricing preferences to the backend itinerary pricing DTO.
 *
 * Supersaver fares are never requested: they are non-refundable, which conflicts
 * with the purchase flow's refund-on-failure guarantee.
 *
 * @param prefs Persisted pricing preferences from `pricingPrefsSlice`.
 * @returns DTO shape accepted by itinerary search and offer endpoints.
 */
export const toItineraryPricingPreferencesDTO = ( prefs: PricingPrefs ): ItineraryPricingPreferencesDTO => {
    const dto = new ItineraryPricingPreferencesDTO();
    dto.swissReductionCard = prefs.swissReductionCard;
    dto.useSuperSaverIfAvailable = false;
    dto.categoryOfService = prefs.categoryOfService === "FIRST_CLASS" ? "1" : "2";

    return dto;
};

/**
 * Builds the pricing preferences sent with `POST /api/sales/offer`.
 *
 * Shared by the itinerary list (offer evaluation) and the ticket purchase
 * wizard (offer refresh) so both request offers with identical preferences.
 * The passenger birthdate (from the session JWT) prices age-dependent fares the
 * same way the prebooking will charge them.
 *
 * @param pricingPrefs Persisted pricing preferences from `pricingPrefsSlice`.
 * @param bikePrefs Persisted bike preferences from `bikePrefsSlice` (personal bike transport option).
 * @param passengerBirthdate - Passenger `YYYY-MM-DD` birthdate from the session JWT, when logged in.
 * @returns Offer pricing preferences DTO.
 */
export const buildOfferPricingPreferences = (
    pricingPrefs: PricingPrefs,
    bikePrefs: Pick<BikePrefs, "isPersonalBike" | "transportPersonalBike">,
    passengerBirthdate?: string | null,
): ItineraryPricingPreferencesDTO => {
    const dto = toItineraryPricingPreferencesDTO( pricingPrefs );
    dto.transportPersonalBike = bikePrefs.isPersonalBike === true && bikePrefs.transportPersonalBike === true;
    dto.passengerBirthdate = passengerBirthdate ?? undefined;

    return dto;
};
